/**
 * HTML and JavaScript run inside an iframe with `sandbox="allow-scripts ..."` and no `allow-same-origin`,
 * which gives the page an opaque origin. Code running there cannot read Cue's storage (including API keys)
 * or reach the parent document; it can only post messages, which is how console output gets back.
 */

export const SANDBOX_SOURCE = 'cue-sandbox';

/** The one sandbox policy for every frame that runs user code, docked or in the preview window. */
export const SANDBOX_POLICY = 'allow-scripts allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox';

export interface SandboxMessage {
  source: typeof SANDBOX_SOURCE;
  /** `scroll` reports the page's scroll position as "x,y", so a live update can put the reader back. */
  level: 'log' | 'info' | 'warn' | 'error' | 'done' | 'scroll';
  text: string;
}

export interface ScrollPosition {
  x: number;
  y: number;
}

const preludeScript = (scroll: ScrollPosition | null) => `
(function () {
  var send = function (level, args) {
    try { parent.postMessage({ source: ${JSON.stringify(SANDBOX_SOURCE)}, level: level, text: args.map(fmt).join(' ') }, '*'); } catch (e) {}
  };
  var fmt = function (value) {
    if (typeof value === 'string') return value;
    if (value instanceof Error) return value.stack || value.message;
    try { var json = JSON.stringify(value); return json === undefined ? String(value) : json; } catch (e) { return String(value); }
  };
  ['log', 'info', 'warn', 'error', 'debug'].forEach(function (level) {
    var original = console[level];
    console[level] = function () {
      var args = Array.prototype.slice.call(arguments);
      send(level === 'debug' ? 'log' : level, args);
      if (original) original.apply(console, args);
    };
  });
  window.addEventListener('error', function (event) {
    send('error', [event.error && event.error.stack ? event.error.stack : event.message]);
  });
  window.addEventListener('unhandledrejection', function (event) {
    var reason = event.reason;
    send('error', ['Unhandled promise rejection: ' + (reason && reason.stack ? reason.stack : String(reason))]);
  });
  var timer = 0;
  window.addEventListener('scroll', function () {
    clearTimeout(timer);
    timer = setTimeout(function () { send('scroll', [Math.round(scrollX) + ',' + Math.round(scrollY)]); }, 100);
  }, { passive: true });${
    scroll ? `
  // At load a new frame may not be laid out yet (zero height), and scrolling then does nothing; wait for a size.
  var tries = 0;
  var restore = function () {
    if (innerHeight === 0 && tries++ < 60) { requestAnimationFrame(restore); return; }
    scrollTo(${Math.round(scroll.x)}, ${Math.round(scroll.y)});
  };
  window.addEventListener('load', restore);` : ''
  }
})();
`;

function escapeScriptClose(code: string): string {
  return code.replace(/<\/script/gi, '<\\/script');
}

/** `scroll` restores where the reader was, for a live update of a page they had scrolled. */
export function buildSandboxDocument(kind: 'html' | 'javascript', code: string, scroll: ScrollPosition | null = null): string {
  const prelude = `<script>${preludeScript(scroll)}</script>`;
  if (kind === 'javascript') {
    return `<!doctype html><html><head><meta charset="utf-8">${prelude}<style>body{font-family:var(--font-mono,monospace);margin:12px;color:#333}</style></head><body><script type="module">${escapeScriptClose(code)}\n</script><script type="module">parent.postMessage({source:${JSON.stringify(SANDBOX_SOURCE)},level:'done',text:''},'*');</script></body></html>`;
  }
  const headMatch = /<head[^>]*>/i.exec(code);
  if (headMatch) {
    const at = headMatch.index + headMatch[0].length;
    return `${code.slice(0, at)}${prelude}${code.slice(at)}`;
  }
  return `${prelude}${code}`;
}

export function isSandboxMessage(data: unknown): data is SandboxMessage {
  return Boolean(data && typeof data === 'object' && (data as { source?: string }).source === SANDBOX_SOURCE);
}
